import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Edit, FileDown, Plus, Power, Search, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    ListStatusFilter,
    ListTag,
    Vendor,
    VENDOR_MODULES,
} from '../../types/lists.types';
import { vendorsApi } from '../../utils/lists-api';
import { cn } from '../../components/ui/utils';
import { TAG_PILL_CLASS, orDash } from './utils/lists-utils';
import StatusPill from './components/StatusPill';
import VendorFormDialog from './components/VendorFormDialog';

const ALL_MODULES = 'all';

export default function VendorsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.LIST_VENDOR);

    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [tags, setTags] = useState<ListTag[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ListStatusFilter>('active');
    const [moduleFilter, setModuleFilter] = useState<string>(ALL_MODULES);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Vendor | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // The API forces Active when a module filter is applied, since inactive
            // vendors never appear in another module's selector.
            const params =
                moduleFilter === ALL_MODULES ? { status } : { module: moduleFilter };
            const [rows, tagRows] = await Promise.all([
                vendorsApi.list(params),
                vendorsApi.tags(),
            ]);
            setVendors(rows);
            setTags(tagRows);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load vendors');
        } finally {
            setLoading(false);
        }
    }, [status, moduleFilter]);

    useEffect(() => {
        if (canRead) fetchData();
    }, [canRead, fetchData]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return vendors;
        return vendors.filter((v) =>
            [v.tradingName, v.legalName, v.contactName, v.email]
                .filter(Boolean)
                .some((x) => (x as string).toLowerCase().includes(q))
        );
    }, [vendors, search]);

    const handleToggleStatus = async (vendor: Vendor) => {
        try {
            await vendorsApi.setStatus(vendor.id, !vendor.isActive);
            toast.success(`${vendor.tradingName} set to ${vendor.isActive ? 'Inactive' : 'Active'}`);
            fetchData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update status');
        }
    };

    const handleExport = async (format: 'csv' | 'excel') => {
        try {
            await vendorsApi.export(format);
            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch {
            toast.error('Failed to generate export file');
        }
    };

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (vendor: Vendor) => { setEditing(vendor); setDialogOpen(true); };

    if (!canRead) return <AccessRestricted />;

    const pills = (items: string[]) =>
        items.length === 0 ? (
            <span className="text-muted-foreground">–</span>
        ) : (
            <div className="flex flex-wrap gap-1">
                {items.map((t) => <span key={t} className={TAG_PILL_CLASS}>{t}</span>)}
            </div>
        );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Vendors</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Shared vendor records. A vendor appears in a module only when ticked and Active.
                    </p>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.EXPORT} a={RESOURCES.LIST_VENDOR}>
                        <Button
                            variant="outline"
                            onClick={() => handleExport('csv')}
                            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
                        >
                            <FileDown size={16} /><span>Export CSV</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.CREATE} a={RESOURCES.LIST_VENDOR}>
                        <Button
                            onClick={openAdd}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>Add Vendor</span>
                        </Button>
                    </Can>
                </div>
            </div>

            {/* Active / Inactive tabs — disabled while filtering by module, which implies Active */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as ListStatusFilter)} className="w-full">
                <TabsList className={cn(
                    'bg-gray-100 p-1 rounded-full w-full md:w-fit',
                    moduleFilter !== ALL_MODULES && 'opacity-50 pointer-events-none'
                )}>
                    <TabsTrigger value="active" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Active
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Inactive
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search + module filter */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search vendors..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[250px] min-w-0">
                    <Select value={moduleFilter} onValueChange={setModuleFilter}>
                        <SelectTrigger className="w-full bg-white truncate overflow-hidden">
                            <SelectValue placeholder="Filter by Module" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_MODULES}>All Modules</SelectItem>
                            {VENDOR_MODULES.map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {moduleFilter !== ALL_MODULES && (
                <p className="text-xs text-muted-foreground">
                    Showing Active vendors ticked for <span className="font-medium">{moduleFilter}</span>.
                </p>
            )}

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No vendors found</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((v) => (
                            <Card key={v.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Truck size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium truncate">{v.tradingName}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">{v.legalName}</p>
                                    </div>
                                    <StatusPill isActive={v.isActive} />
                                </div>
                                <div className="mt-3 space-y-1 text-sm">
                                    <p className="text-muted-foreground">{orDash(v.category)} · {orDash(v.contactName)}</p>
                                    <p className="text-muted-foreground">{orDash(v.email)}</p>
                                    <div className="pt-1">{pills(v.tags.map((t) => t.name))}</div>
                                    <div className="pt-1">{pills(v.showInModules)}</div>
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_VENDOR}>
                                        <Button variant="outline" size="icon" onClick={() => openEdit(v)} aria-label="Edit">
                                            <Edit size={16} />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleToggleStatus(v)}
                                            aria-label={v.isActive ? 'Set inactive' : 'Set active'}
                                            className={cn(v.isActive && 'text-destructive')}
                                        >
                                            <Power size={16} />
                                        </Button>
                                    </Can>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Trading Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Legal Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Category</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Contact</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Tags</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Show in Modules</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((v) => (
                                        <TableRow key={v.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Truck size={16} className="text-muted-foreground" />
                                                    {v.tradingName}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">{v.legalName}</TableCell>
                                            <TableCell>{orDash(v.category)}</TableCell>
                                            <TableCell>
                                                <div>{orDash(v.contactName)}</div>
                                                {v.email && (
                                                    <a href={`mailto:${v.email}`} className="text-xs hover:underline text-primary">
                                                        {v.email}
                                                    </a>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[200px]">
                                                {pills(v.tags.map((t) => t.name))}
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[220px]">
                                                {pills(v.showInModules)}
                                            </TableCell>
                                            <TableCell><StatusPill isActive={v.isActive} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_VENDOR}>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(v)} aria-label="Edit">
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => handleToggleStatus(v)}
                                                            aria-label={v.isActive ? 'Set inactive' : 'Set active'}
                                                            className={cn(v.isActive && 'text-destructive')}
                                                        >
                                                            <Power size={16} />
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <VendorFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                vendor={editing}
                tags={tags}
                onSaved={fetchData}
            />
        </div>
    );
}
